import React from 'react';

import AdherenceDistributionGraph from "../../../components/Graphs/AdherenceDistributionGraph";
import Strings from "../../../Strings";
import { roundToDecimal } from "../../../utils/unitConverters";
import { graphContainer } from "../../Dashboards/Widgets/graphContainers";
import {
  EnrollmentsWithNoCptCodesRpmMetadata,
  EnrollmentsWithNoCptCodesRtmMetadata,
  PatientsWith20minCodeRtmMetadata,
  PatientsWith40minCodeRtmMetadata,
  PatientsWith60minCodeRtmMetadata,
  PatientsWoMonitoringCodeRtmRespMetadata,
  PatientsWoMonitoringCodeRtmMsMetadata,
  PatientsWoMonitoringCodeRtmBehavMetadata,
  PatientsWith20minCodeRpmMetadata,
  PatientsWith40minCodeRpmMetadata,
  PatientsWith60minCodeRpmMetadata,
  PatientsWoMonitoringCodeRpmMetadata,
  NewEnrollmentsRpmMetadata,
  NewEnrollmentsRtmMetadata,
  AvgEnrollmentTimeRpmMetadata,
  AvgEnrollmentTimeRtmMetadata,
  AdherenceDistributionMetadata,
  RTMCptCodesDistributionMetadata,
  RPMCptCodesDistributionMetadata,
  RTMNumberOfCptCodesMetadata,
  RPMNumberOfCptCodesMetadata,
  RTMTimeCodesMetadata,
  RPMTimeCodesMetadata,
  RTMMonitoringCodesDistributionMetadata,
  RPMMonitoringCodesDistributionMetadata,
  numberWithTitleWidget,
} from './reportWigetsMetadata';
import CptCodesDistributionGraph from '../../../components/Graphs/CptCodesDistributionGraph';
import EsiActivePillboxUsers, { getActiveMonthlyPillboxUsersMetadata } from '../../Dashboards/Widgets/EsiActivePillboxUsers';
import { isSuperUser } from '../../../utils/userRoles';
import EsiPatientStats, { getEsiPatientsStateMetadata } from '../../Dashboards/Widgets/EsiPatientStats';
import TimeCodesGraph from '../../../components/Graphs/TimeCodesGraph';
import MonitoringCodesGraph from '../../../components/Graphs/MonitoringCodesGraph';
import { chartTypeEnum } from '../../../components/Graphs/GenericCategoryGraph';
import { TotalKitsMetadata } from '../../Dashboards/DashboardRPM';
import TotalKits from '../../Dashboards/Widgets/TotalKits';
import PatientStatus, { PatientStatusMetadata } from '../../Dashboards/Widgets/PatientStatus';
import PatientStatusInTime, { PatientStatusInTimeMetadata } from '../../Dashboards/Widgets/PatientStatusInTime';
import MetricTrend, { MetricsForTrendPlot, getMetricTrendMetadata } from '../../Dashboards/Widgets/MetricTrend';
import MetricTrendAllIntervals, { getTrendAllIntervalsMetadata } from '../../Dashboards/Widgets/MetricTrendAllIntervals';
import StackedMetricTrend, { getStackedMetricTrendMetadata, MetricsForTrendPlotStacked } from '../../Dashboards/Widgets/StackedMetricTrend';
import MetricTrendTable from '../../Dashboards/Widgets/MetricTrendTable';
import PatientsWithNoReadingsForXDays, { PatientsWithNoReadingsForXDaysMetadata } from '../../Dashboards/Widgets/PatientsWithNoReadingsForXDays';

export default function initializeDashboard(
  dataObj, data30daysObj, enrollmentMetricsObj, setWidgets, configuration, colorPalette, dashboardSettingsFeature, refreshTimestamp, widgetResizeTimestamps) {
  const data = dataObj?.data || [];
  const data30days = data30daysObj?.data || {};
  const enrollmentMetrics = enrollmentMetricsObj?.data || {};
  const loadingData = !dataObj?.loaded;
  const loadingData30days = !data30daysObj?.loaded;
  const loadingEnrollmentMetrics = !enrollmentMetricsObj?.loaded;
  const dataFormatted = data ? {
    months: data.reduce((acc, d) => ({ ...acc, [d.periodNumber]: d }), {}),
  } : {};

  const widgetEwoCptCodeRpm = numberWithTitleWidget(
    EnrollmentsWithNoCptCodesRpmMetadata,
    data30days?.RPM?.enrollments_without_cpt_codes,
    dashboardSettingsFeature,
    roundToDecimal(data30days?.RPM?.percent_of_enrollments_without_cpt_codes, 2),
  );
  const widgetEwoCptCodeRtm = numberWithTitleWidget(
    EnrollmentsWithNoCptCodesRtmMetadata,
    data30days?.RTM?.enrollments_without_cpt_codes, 
    dashboardSettingsFeature,
    roundToDecimal(data30days?.RTM?.percent_of_enrollments_without_cpt_codes, 2),
  );
  const widget20minCodeRtm = numberWithTitleWidget(
    PatientsWith20minCodeRtmMetadata,
    data30days?.RTM?.cpt_codes_statistics?.['CPT 98980']?.qualified_enrollments,
    dashboardSettingsFeature,
    roundToDecimal(data30days?.RTM?.cpt_codes_statistics?.['CPT 98980']?.percent_qualified, 2),
  );
  const widget40minCodeRtm = numberWithTitleWidget(
    PatientsWith40minCodeRtmMetadata,
    data30days?.RTM?.cpt_codes_statistics?.['CPT 98981 #1'].qualified_enrollments,
    dashboardSettingsFeature,
    roundToDecimal(data30days?.RTM?.cpt_codes_statistics?.['CPT 98981 #1'].percent_qualified, 2),
  );
  const widget60minCodeRtm = numberWithTitleWidget(
    PatientsWith60minCodeRtmMetadata,
    data30days?.RTM?.cpt_codes_statistics?.['CPT 98981 #2'].qualified_enrollments,
    dashboardSettingsFeature,
    roundToDecimal(data30days?.RTM?.cpt_codes_statistics?.['CPT 98981 #2'].percent_qualified, 2),
  );
  const widgetmonitCodeRtmResp = numberWithTitleWidget(
    PatientsWoMonitoringCodeRtmRespMetadata,
    (data30days?.RTM?.total_patients_per_subprogram?.RTMRespiratory || 0) -
      (data30days?.RTM?.cpt_codes_statistics?.['CPT 98976']?.qualified_enrollments || 0),
    dashboardSettingsFeature,
    roundToDecimal(
      100 - (data30days?.RTM?.cpt_codes_statistics?.['CPT 98976']?.percent_qualified ?? 0),
      2
    ),
  );
  const widgetmonitCodeRtmMs = numberWithTitleWidget(
    PatientsWoMonitoringCodeRtmMsMetadata,
    (data30days?.RTM?.total_patients_per_subprogram?.RTMMusculoskeletal ?? 0) -
      (data30days?.RTM?.cpt_codes_statistics?.['CPT 98977']?.qualified_enrollments ?? 0),
    dashboardSettingsFeature,
    roundToDecimal(100 - (data30days?.RTM?.cpt_codes_statistics?.['CPT 98977']?.percent_qualified ?? 0), 2),
  );
  const widgetmonitCodeRtmBehav = numberWithTitleWidget(
    PatientsWoMonitoringCodeRtmBehavMetadata,
    (data30days?.RTM?.total_patients_per_subprogram?.RTMBehavioralHealth ?? 0) -
      (data30days?.RTM?.cpt_codes_statistics?.['CPT 98978']?.qualified_enrollments ?? 0),
    dashboardSettingsFeature,
    roundToDecimal(100 - (data30days?.RTM?.cpt_codes_statistics?.['CPT 98978']?.percent_qualified ?? 0), 2),
  );
  const widget20minCodeRpm = numberWithTitleWidget(
    PatientsWith20minCodeRpmMetadata,
    data30days?.RPM?.cpt_codes_statistics?.['CPT 99457']?.qualified_enrollments,
    dashboardSettingsFeature,
    roundToDecimal(data30days?.RPM?.cpt_codes_statistics?.['CPT 99457']?.percent_qualified, 2),
  );
  const widget40minCodeRpm = numberWithTitleWidget(
    PatientsWith40minCodeRpmMetadata,
    data30days?.RPM?.cpt_codes_statistics?.['CPT 99458 #1'].qualified_enrollments,
    dashboardSettingsFeature,
    roundToDecimal(data30days?.RPM?.cpt_codes_statistics?.['CPT 99458 #1'].percent_qualified, 2),
  );
  const widget60minCodeRpm = numberWithTitleWidget(
    PatientsWith60minCodeRpmMetadata,
    data30days?.RPM?.cpt_codes_statistics?.['CPT 99458 #2'].qualified_enrollments,
    dashboardSettingsFeature,
    roundToDecimal(data30days?.RPM?.cpt_codes_statistics?.['CPT 99458 #2'].percent_qualified, 2),
  );
  const widgetMonitCodeRpm = numberWithTitleWidget(
    PatientsWoMonitoringCodeRpmMetadata,
    (data30days?.RPM?.total_enrollments ?? 0) - (data30days?.RPM?.cpt_codes_statistics?.['CPT 99454']?.qualified_enrollments ?? 0),
    dashboardSettingsFeature,
    roundToDecimal(100 - (data30days?.RPM?.cpt_codes_statistics?.['CPT 99454']?.percent_qualified ?? 0), 2),
  );
  const widgetNewEnrollmentsRpm = numberWithTitleWidget(
    NewEnrollmentsRpmMetadata,
    data30days?.RPM?.new_enrollments,
    dashboardSettingsFeature,
  );
  const widgetNewEnrollmentsRtm = numberWithTitleWidget(
    NewEnrollmentsRtmMetadata,
    data30days?.RTM?.new_enrollments,
    dashboardSettingsFeature,
  );
  const widgetAvgEnrollemntTimeRpm = numberWithTitleWidget(
    AvgEnrollmentTimeRpmMetadata,
    data30days?.RPM?.average_enrollment_time
      ? roundToDecimal((data30days?.RPM?.average_enrollment_time ?? 0) / (60 * 60 * 60), 0)
      : 0,
    dashboardSettingsFeature,
    undefined,
    ` ${Strings.days}`,
  );
  const widgetAvgEnrollemntTimeRtm = numberWithTitleWidget(
    AvgEnrollmentTimeRtmMetadata,
    data30days?.RTM?.average_enrollment_time
      ? roundToDecimal((data30days?.RTM?.average_enrollment_time ?? 0) / (60 * 60 * 60), 0)
      : 0,
    dashboardSettingsFeature,
    undefined,
    ` ${Strings.days}`,
  );

  const widgetsMetricTrends = Object.values(MetricsForTrendPlot).map(metric => {
    const metadata = getMetricTrendMetadata(metric.id);
    return {
      ...metadata,
      noPadding: true,
      render: (
        <MetricTrend
          data={enrollmentMetrics}
          colorPalette={colorPalette}
          graphType={configuration?.[metadata.id]?.chartType?.value || metadata.configuration.chartType.defaultValue}
          rollingPeriod={configuration?.[metadata.id]?.rollingPeriod?.value || metadata.configuration.rollingPeriod.defaultValue}
          plotInterval={configuration?.[metadata.id]?.plotInterval?.value || metadata.configuration.plotInterval?.defaultValue}
          metric={metric.id}
        />
      ),
      loading: loadingEnrollmentMetrics,
    };
  });

  const widgetsMetricMultiIntervalsTrends = Object.values(MetricsForTrendPlot).map(metric => {
    const metadata = getTrendAllIntervalsMetadata(metric.id);
    return {
      ...metadata,
      noPadding: true,
      render: (
        <MetricTrendAllIntervals
          data={enrollmentMetrics}
          colorPalette={colorPalette}
          graphType={configuration?.[metadata.id]?.chartType?.value || metadata.configuration.chartType.defaultValue}
          rollingPeriod={configuration?.[metadata.id]?.rollingPeriod?.value || metadata.configuration.rollingPeriod.defaultValue}
          metric={metric.id}
        />
      ),
      loading: loadingEnrollmentMetrics,
    };
  });

  const widgetsMetricStackedTrends = Object.values(MetricsForTrendPlotStacked).map(metric => {
    const metadata = getStackedMetricTrendMetadata(metric.id);
    return {
      ...metadata,
      noPadding: true,
      render: configuration?.[metadata.id]?.chartType?.value === 'table' ? (
        <MetricTrendTable
          data={[MetricsForTrendPlotStacked.patientsPerProgram.id, MetricsForTrendPlotStacked.adherence.id].includes(metric.id) ? dataFormatted : enrollmentMetrics}
          rollingPeriod={configuration?.[metadata.id]?.rollingPeriod?.value || metadata.configuration.rollingPeriod.defaultValue}
          plotInterval={configuration?.[metadata.id]?.plotInterval?.value || metadata.configuration.plotInterval?.defaultValue}
          metric={metric.id}
          metrics={configuration?.[metadata.id]?.metrics?.value || metadata.configuration.metrics.defaultValue}
          widgetResizeTimestamp={widgetResizeTimestamps[metadata.id]}
        />
      ) : (
        <StackedMetricTrend
          data={[MetricsForTrendPlotStacked.patientsPerProgram.id, MetricsForTrendPlotStacked.adherence.id].includes(metric.id) ? dataFormatted : enrollmentMetrics}
          colorPalette={colorPalette}
          graphType={configuration?.[metadata.id]?.chartType?.value || metadata.configuration.chartType.defaultValue}
          rollingPeriod={configuration?.[metadata.id]?.rollingPeriod?.value || metadata.configuration.rollingPeriod.defaultValue}
          plotInterval={configuration?.[metadata.id]?.plotInterval?.value || metadata.configuration.plotInterval?.defaultValue}
          metric={metric.id}
          metrics={configuration?.[metadata.id]?.metrics?.value || metadata.configuration.metrics.defaultValue}
        />
      ),
      loading: loadingEnrollmentMetrics,
    };
  });

  const widgetAD = {
    ...AdherenceDistributionMetadata,
    noPadding: true,
    render: graphContainer(
      <AdherenceDistributionGraph
        graphData={
          data30days?.RTM?.adherence_distribution
            ? Object.entries(data30days?.RTM?.adherence_distribution).map(([range, adherence]) => ({
                range,
                adherence,
              }))
            : []
        }
        colorPalette={colorPalette}
        graphType={
          configuration?.[AdherenceDistributionMetadata.id]?.chartType?.value ||
          AdherenceDistributionMetadata.configuration.chartType.defaultValue
        }
      />,
      'Adherence Distribution',
    ),
  };

  const dataForCodesDistributionRTM = [0, 1, 2, 3, 4].map(number => ({
    codes: `${number} ${number === 1 ? 'code' : 'codes'}`,
    count: data30days?.RTM?.cpt_codes_distribution?.[number] || 0,
  }));
  const widgetCodesDistrRTM = {
    ...RTMCptCodesDistributionMetadata,
    noPadding: true,
    render: graphContainer(
      <CptCodesDistributionGraph
        graphData={dataForCodesDistributionRTM}
        colorPalette={colorPalette}
        graphType={
          configuration?.[RTMCptCodesDistributionMetadata.id]?.chartType?.value ||
          RTMCptCodesDistributionMetadata.configuration.chartType.defaultValue
        }
      />,
      'RTM CPT Codes Distribution',
    ),
  };
  const dataForCodesDistributionRPM = [0, 1, 2, 3, 4].map(number => ({
    codes: `${number} ${number === 1 ? 'code' : 'codes'}`,
    count: data30days?.RPM?.cpt_codes_distribution?.[number] || 0,
  }));
  const widgetCodesDistrRPM = {
    ...RPMCptCodesDistributionMetadata,
    noPadding: true,
    render: graphContainer(
      <CptCodesDistributionGraph
        graphData={dataForCodesDistributionRPM}
        colorPalette={colorPalette}
        graphType={
          configuration?.[RPMCptCodesDistributionMetadata.id]?.chartType?.value ||
          RPMCptCodesDistributionMetadata.configuration.chartType.defaultValue
        }
      />,
      'RPM CPT Codes Distribution',
    ),
  };

  const dataForNumberOfCodesRTM = data30days?.RTM?.cpt_codes_statistics ? Object.keys(data30days?.RTM?.cpt_codes_statistics).map(code => ({
    codes: code,
    count: data30days?.RTM?.cpt_codes_statistics?.[code]?.qualified_enrollments || 0,
  })) : [];
  const widgetNumberOfCodesRTM = {
    ...RTMNumberOfCptCodesMetadata,
    noPadding: true,
    render: graphContainer(
      <CptCodesDistributionGraph
        graphData={dataForNumberOfCodesRTM}
        colorPalette={colorPalette}
        tooltipTitle="Number of Codes"
        graphType={
          configuration?.[RTMNumberOfCptCodesMetadata.id]?.chartType?.value ||
          RTMNumberOfCptCodesMetadata.configuration.chartType.defaultValue
        }
      />,
      'Number of CPT Codes (RTM)',
    ),
  };
  const dataForNumberOfCodesRPM = data30days?.RPM?.cpt_codes_statistics ? Object.keys(data30days?.RPM?.cpt_codes_statistics).map(code => ({
    codes: code,
    count: data30days?.RPM?.cpt_codes_statistics?.[code]?.qualified_enrollments || 0,
  })) : [];
  const widgetNumberOfCodesRPM = {
    ...RPMNumberOfCptCodesMetadata,
    noPadding: true,
    render: graphContainer(
      <CptCodesDistributionGraph
        graphData={dataForNumberOfCodesRPM}
        colorPalette={colorPalette}
        tooltipTitle="Number of Codes"
        graphType={
          configuration?.[RPMNumberOfCptCodesMetadata.id]?.chartType?.value ||
          RPMNumberOfCptCodesMetadata.configuration.chartType.defaultValue
        }
      />,
      'Number of CPT Codes (RPM)',
    ),
  };

  const ActiveMonthlyPillboxUsersMetadata = getActiveMonthlyPillboxUsersMetadata(isSuperUser());
  const widgetActiveMonthlyPillboxUsers = {
    ...ActiveMonthlyPillboxUsersMetadata,
    noPadding: true,
    noBorder: true,
    configuration: dashboardSettingsFeature ? ActiveMonthlyPillboxUsersMetadata.configuration : undefined,
    render: <EsiActivePillboxUsers />,
  };

  const EsiPatientsStateMetadata = getEsiPatientsStateMetadata(isSuperUser());
  const widgetgetEsiPatientsState = {
    ...EsiPatientsStateMetadata,
    noPadding: true,
    render: <EsiPatientStats colorPalette={colorPalette} />,
  };

  const widgetTimesCodesRTM = {
    ...RTMTimeCodesMetadata,
    noPadding: true,
    render: graphContainer(
      <TimeCodesGraph
        graphData={
          data30days
            ? [
                {
                  code: '20 min',
                  enrollmentsWithCode: data30days?.RTM?.cpt_codes_statistics?.['CPT 98980']?.qualified_enrollments || 0,
                  enrollmentsWithoutCode:
                    (data30days?.RTM?.total_enrollments ?? 0) -
                      (data30days?.RTM?.cpt_codes_statistics?.['CPT 98980']?.qualified_enrollments ?? 0),
                },
                {
                  code: '40 min',
                  enrollmentsWithCode: data30days?.RTM?.cpt_codes_statistics?.['CPT 98981 #1']?.qualified_enrollments || 0,
                  enrollmentsWithoutCode:
                    (data30days?.RTM?.total_enrollments ?? 0) -
                      (data30days?.RTM?.cpt_codes_statistics?.['CPT 98981 #1']?.qualified_enrollments ?? 0),
                },
                {
                  code: '60 min',
                  enrollmentsWithCode: data30days?.RTM?.cpt_codes_statistics?.['CPT 98981 #2']?.qualified_enrollments || 0,
                  enrollmentsWithoutCode:
                    (data30days?.RTM?.total_enrollments ?? 0) -
                      (data30days?.RTM?.cpt_codes_statistics?.['CPT 98981 #2']?.qualified_enrollments ?? 0),
                },
              ]
            : []
        }
        colorPalette={colorPalette}
        graphType={
          configuration?.[RTMTimeCodesMetadata.id]?.stackedChartType?.value ||
          RTMTimeCodesMetadata.configuration.stackedChartType.defaultValue
        }
      />,
      'RTM 20, 40, 60 Minutes Codes Distribution',
    ),
  };

  const widgetTimesCodesRPM = {
    ...RPMTimeCodesMetadata,
    noPadding: true,
    render: graphContainer(
      <TimeCodesGraph
        graphData={
          data30days
            ? [
                {
                  code: '20 min',
                  enrollmentsWithCode: data30days?.RPM?.cpt_codes_statistics?.['CPT 99457']?.qualified_enrollments || 0,
                  enrollmentsWithoutCode:
                    (data30days?.RPM?.total_enrollments ?? 0) -
                      (data30days?.RPM?.cpt_codes_statistics?.['CPT 99457']?.qualified_enrollments ?? 0),
                },
                {
                  code: '40 min',
                  enrollmentsWithCode: data30days?.RPM?.cpt_codes_statistics?.['CPT 99458 #1']?.qualified_enrollments || 0,
                  enrollmentsWithoutCode:
                    (data30days?.RPM?.total_enrollments ?? 0) -
                      (data30days?.RPM?.cpt_codes_statistics?.['CPT 99458 #1']?.qualified_enrollments ?? 0),
                },
                {
                  code: '60 min',
                  enrollmentsWithCode: data30days?.RPM?.cpt_codes_statistics?.['CPT 99458 #2']?.qualified_enrollments || 0,
                  enrollmentsWithoutCode:
                    (data30days?.RPM?.total_enrollments ?? 0) -
                      (data30days?.RPM?.cpt_codes_statistics?.['CPT 99458 #2']?.qualified_enrollments ?? 0),
                },
              ]
            : []
        }
        colorPalette={colorPalette}
        graphType={
          configuration?.[RPMTimeCodesMetadata.id]?.stackedChartType?.value ||
          RPMTimeCodesMetadata.configuration.stackedChartType.defaultValue
        }
      />,
      'RPM 20, 40, 60 Minutes Codes Distribution',
    ),
  };

  const widgetNoMonitCodesRTM = {
    ...RTMMonitoringCodesDistributionMetadata,
    noPadding: true,
    render: graphContainer(
      <MonitoringCodesGraph
        graphData={
          data30days
            ? [
              {
                  program: 'RTM',
                  hasCode:
                    (data30days?.RTM?.cpt_codes_statistics?.['CPT 98976']?.qualified_enrollments ?? 0) +
                      (data30days?.RTM?.cpt_codes_statistics?.['CPT 98977']?.qualified_enrollments ?? 0) +
                      (data30days?.RTM?.cpt_codes_statistics?.['CPT 98978']?.qualified_enrollments ?? 0),
                  doesntHaveCode:
                    ((data30days?.RTM?.total_patients_per_subprogram?.RTMRespiratory ?? 0) +
                      (data30days?.RTM?.total_patients_per_subprogram?.RTMMusculoskeletal ?? 0) +
                      (data30days?.RTM?.total_patients_per_subprogram?.RTMBehavioralHealth ?? 0)) -
                      ((data30days?.RTM?.cpt_codes_statistics?.['CPT 98976']?.qualified_enrollments ?? 0) +
                        (data30days?.RTM?.cpt_codes_statistics?.['CPT 98977']?.qualified_enrollments ?? 0) +
                        (data30days?.RTM?.cpt_codes_statistics?.['CPT 98978']?.qualified_enrollments ?? 0)),
                },
                {
                  program: 'RTM Respiratory',
                  parent: 'RTM',
                  hasCode: data30days?.RTM?.cpt_codes_statistics?.['CPT 98976']?.qualified_enrollments ?? 0,
                  doesntHaveCode:
                    (data30days?.RTM?.total_patients_per_subprogram?.RTMRespiratory ?? 0) -
                      (data30days?.RTM?.cpt_codes_statistics?.['CPT 98976']?.qualified_enrollments ?? 0),
                },
                {
                  program: 'RTM Musculoskeletal',
                  parent: 'RTM',
                  hasCode: data30days?.RTM?.cpt_codes_statistics?.['CPT 98977']?.qualified_enrollments ?? 0,
                  doesntHaveCode:
                    (data30days?.RTM?.total_patients_per_subprogram?.RTMMusculoskeletal ?? 0) -
                      (data30days?.RTM?.cpt_codes_statistics?.['CPT 98977']?.qualified_enrollments ?? 0),
                },
                {
                  program: 'RTM Cognitive Behavioral',
                  parent: 'RTM',
                  hasCode: data30days?.RTM?.cpt_codes_statistics?.['CPT 98978']?.qualified_enrollments ?? 0,
                  doesntHaveCode:
                    (data30days?.RTM?.total_patients_per_subprogram?.RTMBehavioralHealth ?? 0) -
                      (data30days?.RTM?.cpt_codes_statistics?.['CPT 98978']?.qualified_enrollments ?? 0),
                },
              ]
            : []
        }
        colorPalette={colorPalette}
        graphType={
          configuration?.[RTMMonitoringCodesDistributionMetadata.id]?.chartType?.value ||
          RTMMonitoringCodesDistributionMetadata.configuration.chartType.defaultValue
        }
      />,
      'RTM Monitoring Codes Distribution',
    ),
  };

  const widgetNoMonitCodesRPM = {
    ...RPMMonitoringCodesDistributionMetadata,
    noPadding: true,
    render: graphContainer(
      <MonitoringCodesGraph
        graphData={
          data30days
            ? [
              {
                category: 'Enrollments with Monitoring Code',
                count: data30days?.RPM?.cpt_codes_statistics?.['CPT 99454']?.qualified_enrollments || 0,
              },
              {
                category: 'Enrollments without Monitoring Code',
                count:
                  (data30days?.RPM?.total_enrollments ?? 0) -
                    (data30days?.RPM?.cpt_codes_statistics?.['CPT 99454']?.qualified_enrollments ?? 0),
              }]
            : []
        }
        rpm
        colorPalette={colorPalette}
        graphType={chartTypeEnum.pie}
      />,
      'RPM Monitoring Codes Distribution',
    ),
  };

  // const widgetTK = {
  //   ...TotalKitsMetadata,
  //   render: <TotalKits orgId={selectedOrg.masked_id} />,
  // };

  // const widgetPE = {
  //   ...PatientStatusMetadata,
  //   render: <PatientStatus
  //     graphType={
  //       configuration?.[PatientStatusMetadata.id]?.chartType?.value ||
  //       PatientStatusMetadata.configuration.chartType.defaultValue
  //     }
  //     refreshTimestamp={refreshTimestamp}
  //     orgId={selectedOrg.masked_id}
  //   />,
  //   noPadding: true,
  // };

  // const widgetPET = {
  //   ...PatientStatusInTimeMetadata,
  //   render: <PatientStatusInTime
  //     graphType={
  //       configuration?.[PatientStatusInTimeMetadata.id]?.stackedChartType?.value ||
  //       PatientStatusInTimeMetadata.configuration.stackedChartType.defaultValue
  //     }
  //     rollingPeriod={configuration?.[PatientStatusInTimeMetadata.id]?.rollingPeriod?.value || PatientStatusInTimeMetadata.configuration.rollingPeriod.defaultValue}
  //     plotInterval={configuration?.[PatientStatusInTimeMetadata.id]?.plotInterval?.value || PatientStatusInTimeMetadata.configuration.plotInterval.defaultValue}
  //     metrics={configuration?.[PatientStatusInTimeMetadata.id]?.metrics?.value || PatientStatusInTimeMetadata.configuration.metrics.defaultValue}
  //     refreshTimestamp={refreshTimestamp}
  //     orgId={selectedOrg.masked_id}
  //   />,
  //   noPadding: true,
  // };

  const widgetPwnR = {
    ...PatientsWithNoReadingsForXDaysMetadata,
    render: <PatientsWithNoReadingsForXDays
      data={enrollmentMetrics}
      colorPalette={colorPalette}
      graphType={configuration?.[PatientsWithNoReadingsForXDaysMetadata.id]?.chartType?.value || PatientsWithNoReadingsForXDaysMetadata.configuration.chartType.defaultValue}
      //metrics={configuration?.[PatientsWithNoReadingsForXDaysMetadata.id]?.metrics?.value || PatientsWithNoReadingsForXDaysMetadata.configuration.metrics.defaultValue}
    />,
    noPadding: true,
    loading: loadingEnrollmentMetrics,
  };

  setWidgets([
    // widgetEwoCptCodeRpm,
    // widgetEwoCptCodeRtm,
    // widgetAD,
    // widget20minCodeRtm,
    // widget40minCodeRtm,
    // widget60minCodeRtm,
    // widgetmonitCodeRtmResp,
    // widgetmonitCodeRtmMs,
    // widgetmonitCodeRtmBehav,
    // widget20minCodeRpm,
    // widget40minCodeRpm,
    // widget60minCodeRpm,
    // widgetMonitCodeRpm,
    // widgetCodesDistrRTM,
    // widgetCodesDistrRPM,
    // widgetNumberOfCodesRTM,
    // widgetNumberOfCodesRPM,
    // widgetNewEnrollmentsRpm,
    // widgetNewEnrollmentsRtm,
    // widgetAvgEnrollemntTimeRpm,
    // widgetAvgEnrollemntTimeRtm,
    // widgetTimesCodesRTM,
    // widgetTimesCodesRPM,
    // widgetNoMonitCodesRTM,
    // widgetNoMonitCodesRPM,
    // widgetActiveMonthlyPillboxUsers,
    // widgetgetEsiPatientsState,
    // widgetTK,
    // widgetPE,
    ...widgetsMetricTrends,
    ... widgetsMetricMultiIntervalsTrends,
    ...widgetsMetricStackedTrends,
    // widgetPET,
    widgetPwnR,
  ]);
};
